import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import api from '../../services/api';
import { toast } from 'sonner';
import { Clock, Plus, Calendar, Trash2, CheckCircle, XCircle, AlertCircle, DollarSign } from 'lucide-react';
import { formatDateOnly } from '../../utils/timezone';

const DUTY_LABELS = {
  front_desk: 'Front Desk',
  coaching: 'Coaching',
  academy_management: 'Academy Management'
};

const StaffTimeEntries = () => {
  const [entries, setEntries] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    hours_worked: '',
    duty_type: '',
    notes: ''
  });
  
  // Delete confirmation
  const [deleteEntryDialog, setDeleteEntryDialog] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const fetchData = async () => {
    try {
      const [entriesRes, profileRes] = await Promise.all([
        api.get(`/staff/time-entries?month=${selectedMonth}`),
        api.get('/staff/profile/me')
      ]);
      setEntries(entriesRes.data);
      setProfile(profileRes.data);
      
      // Set default duty type if only one assigned
      if (profileRes.data.assigned_duties?.length === 1) {
        setFormData(prev => ({ ...prev, duty_type: profileRes.data.assigned_duties[0] }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.hours_worked || parseFloat(formData.hours_worked) <= 0) {
      toast.error('Please enter valid hours');
      return;
    }
    if (!formData.duty_type) {
      toast.error('Please select a duty type');
      return;
    }
    try {
      await api.post('/staff/time-entries', {
        date: formData.date,
        hours_worked: parseFloat(formData.hours_worked),
        duty_type: formData.duty_type,
        notes: formData.notes || null
      });
      toast.success('Time entry submitted');
      setDialogOpen(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        hours_worked: '',
        duty_type: profile?.assigned_duties?.[0] || '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit time entry');
    }
  };

  const openDeleteDialog = (entryId) => {
    setEntryToDelete(entryId);
    setDeleteEntryDialog(true);
  };

  const handleDelete = async () => {
    if (!entryToDelete) return;
    
    try {
      await api.delete(`/staff/time-entries/${entryToDelete}`);
      toast.success('Entry deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    } finally {
      setDeleteEntryDialog(false);
      setEntryToDelete(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="h-3 w-3" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
            <XCircle className="h-3 w-3" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <AlertCircle className="h-3 w-3" />
            Pending
          </span>
        );
    }
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ value, label });
    }
    return options;
  };

  // Calculate stats by duty type
  const statsByDuty = {};
  entries.forEach(e => {
    const duty = e.duty_type;
    if (!statsByDuty[duty]) {
      statsByDuty[duty] = { total: 0, pending: 0, approved: 0, amount: 0 };
    }
    statsByDuty[duty].total += e.hours_worked;
    if (e.status === 'pending') statsByDuty[duty].pending += e.hours_worked;
    if (e.status === 'approved') {
      statsByDuty[duty].approved += e.hours_worked;
      statsByDuty[duty].amount += e.amount || 0;
    }
  });

  const totalHours = entries.reduce((sum, e) => sum + e.hours_worked, 0);
  const pendingHours = entries.filter(e => e.status === 'pending').reduce((sum, e) => sum + e.hours_worked, 0);
  const approvedAmount = entries.filter(e => e.status === 'approved').reduce((sum, e) => sum + (e.amount || 0), 0);

  if (loading) {
    return (
      <DashboardLayout title="My Hours">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Hours">
      <div className="space-y-6" data-testid="staff-time-entries-page">
        {/* Header with Add Button */}
        <div className="space-y-3">
          <div>
            <p className="text-slate-600">Track and submit your working hours by duty type</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-blue-500 hover:bg-blue-600" 
                data-testid="add-hours-btn"
                disabled={!profile?.assigned_duties?.length}
              >
                <Plus className="h-4 w-4 mr-2" />
                Log Hours
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Log Working Hours</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    required
                    data-testid="entry-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Duty Type *</Label>
                  <Select
                    value={formData.duty_type}
                    onValueChange={(value) => setFormData({ ...formData, duty_type: value })}
                    required
                  >
                    <SelectTrigger data-testid="entry-duty-select">
                      <SelectValue placeholder="Select duty type" />
                    </SelectTrigger>
                    <SelectContent>
                      {profile?.assigned_duties?.map((duty) => (
                        <SelectItem key={duty} value={duty}>
                          {DUTY_LABELS[duty] || duty}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Hours Worked *</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="24"
                    value={formData.hours_worked}
                    onChange={(e) => setFormData({ ...formData, hours_worked: e.target.value })}
                    placeholder="e.g., 4.5"
                    required
                    data-testid="entry-hours-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="What did you work on?"
                    rows={2}
                    data-testid="entry-notes-input"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="submit-entry-btn">
                    Submit
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* No Duties Warning */}
        {(!profile?.assigned_duties || profile.assigned_duties.length === 0) && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">No duties assigned</span>
            </div>
            <p className="text-sm text-amber-600 mt-1">
              Contact your administrator to assign duties before logging hours.
            </p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-2 lg:gap-4">
          <Card className="border border-slate-200">
            <CardContent className="p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-slate-500">Total Hours</p>
                  <p className="font-heading text-xl lg:text-2xl font-bold text-slate-900">{totalHours.toFixed(1)}</p>
                </div>
                <Clock className="h-6 w-6 lg:h-8 lg:w-8 text-blue-500 shrink-0" />
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-slate-500">Pending</p>
                  <p className="font-heading text-xl lg:text-2xl font-bold text-amber-600">{pendingHours.toFixed(1)}</p>
                </div>
                <AlertCircle className="h-6 w-6 lg:h-8 lg:w-8 text-amber-500 shrink-0" />
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-slate-500">Approved</p>
                  <p className="font-heading text-xl lg:text-2xl font-bold text-green-600">{cs()}{approvedAmount.toFixed(2)}</p>
                </div>
                <DollarSign className="h-6 w-6 lg:h-8 lg:w-8 text-green-500 shrink-0" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Breakdown by Duty */}
        {Object.keys(statsByDuty).length > 0 && (
          <Card className="border border-slate-200">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading text-lg">Hours by Duty Type</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(statsByDuty).map(([duty, stats]) => (
                  <div key={duty} className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-medium text-slate-900">{DUTY_LABELS[duty] || duty}</p>
                    <div className="mt-2 space-y-1 text-sm">
                      <p className="text-slate-600">Total: <span className="font-semibold">{stats.total.toFixed(1)} hrs</span></p>
                      <p className="text-amber-600">Pending: {stats.pending.toFixed(1)} hrs</p>
                      <p className="text-green-600">Approved: {stats.approved.toFixed(1)} hrs ({cs()}{stats.amount.toFixed(2)})</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Month Filter & Table */}
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                Time Entries
              </CardTitle>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-48" data-testid="month-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getMonthOptions().map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {entries.length === 0 ? (
              <div className="p-12 text-center">
                <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No time entries for this month</p>
                <p className="text-sm text-slate-400 mt-1">Click "Log Hours" to add your first entry</p>
              </div>
            ) : (
              <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Duty Type</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id} data-testid={`entry-${entry.id}`}>
                        <TableCell className="font-medium">
                          {formatDateOnly(entry.date, {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-sm">
                            {DUTY_LABELS[entry.duty_type] || entry.duty_type}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold">{entry.hours_worked}</span> hrs
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {entry.hourly_rate ? `${cs()}${entry.hourly_rate.toFixed(2)}/hr` : '-'}
                        </TableCell>
                        <TableCell className="font-semibold text-green-600">
                          {entry.amount ? `${cs()}${entry.amount.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(entry.status)}</TableCell>
                        <TableCell>
                          {entry.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(entry.id)}
                              className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                              data-testid={`delete-entry-${entry.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden divide-y divide-slate-100">
                {entries.map((entry) => (
                  <div key={`m-${entry.id}`} className="p-3" data-testid={`entry-mobile-${entry.id}`}>
                    <div className="flex items-start justify-between mb-1.5">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 text-sm">
                          {formatDateOnly(entry.date, { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                        <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs mt-0.5">
                          {DUTY_LABELS[entry.duty_type] || entry.duty_type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {getStatusBadge(entry.status)}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-sm">
                        <span className="font-bold text-slate-900">{entry.hours_worked} hrs</span>
                        {entry.amount ? (
                          <span className="font-semibold text-green-600">{cs()}{entry.amount.toFixed(2)}</span>
                        ) : null}
                      </div>
                      {entry.status === 'pending' && (
                        <Button variant="ghost" size="sm" onClick={() => openDeleteDialog(entry.id)} className="h-7 w-7 p-0 text-rose-500">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    {entry.notes && <p className="text-xs text-slate-500 mt-1 truncate">{entry.notes}</p>}
                  </div>
                ))}
              </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Delete Entry Confirmation Dialog */}
        <AlertDialog open={deleteEntryDialog} onOpenChange={setDeleteEntryDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-600" />
                Delete Time Entry
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this time entry? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default StaffTimeEntries;
